import React, { useEffect } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Toggle } from "@carbon/react";
import { buildStudentHref } from "../../Utils/utils";
import { formatDateForApi, formatDateTime } from "../../Utils/timeutils";

const GET_GROUP_QUERY = gql`
  query GetGroup($id: String!) {
    schoolGroupById(id: $id) {
      __typename
      ... on Group {
        id
        name
        students { id username lastname surname }
        editable
        enabledAt
      }
    }
  }
`;

const GET_GROUP_STATE_QUERY = gql`
  query GetGroupState($groupId: String!, $date: String!, $TimeUid: Int!) {
    getGroupState(groupId: $groupId, date: $date, TimeUid: $TimeUid) {
      studentsStudying { id username lastname surname }
    }
  }
`;

const TOGGLE_GROUP_MUTATION = gql`
  mutation ToggleGroup($id: String!, $groupState: Boolean!) {
    toggleGroup(id: $id, groupState: $groupState) {
      editable
      enabledAt
    }
  }
`;

const ME_QUERY = gql`
  query Me {
    me {
      ... on Teacher {
        username
      }
      ... on Admin {
        username
      }
    }
  }
`;

export default function GroupDashboardContent({ selectedGroup, selectedDate, selectedTimeUnit, autoSelectEnabled }) {
    const formattedDate = formatDateForApi(selectedDate);

    const { data, loading, error, refetch } = useQuery(GET_GROUP_QUERY, {
        variables: { id: selectedGroup },
        skip: !selectedGroup
    });

    const { data: groupStateData, loading: groupStateLoading, error: groupStateError } = useQuery(GET_GROUP_STATE_QUERY, {
        variables: { groupId: selectedGroup, date: formattedDate, TimeUid: selectedTimeUnit ?? 0 },
        skip: !selectedGroup || !formattedDate || selectedTimeUnit == null
    });

    const [toggleGroup] = useMutation(TOGGLE_GROUP_MUTATION, {
        onCompleted: () => refetch()
    });

    const { data: meData } = useQuery(ME_QUERY);

    const handleToggle = (checked) => {
        toggleGroup({ variables: { id: selectedGroup, groupState: checked } });
    };

    useEffect(() => {
        // console.log(groupStateData);
    }, [selectedDate, selectedTimeUnit]);

    if (loading) {
        return (
            <section className="container mx-auto">
                <p className="text-gray-600 mb-4">Loading...</p>
            </section>
        );
    }

    if (error) {
        return (
            <section className="container mx-auto">
                <p className="text-red-600 mb-4">Error fetching data: {error.message}</p>
            </section>
        );
    }

    const group = data?.schoolGroupById;

    if (!group || group.__typename !== "Group") {
        return (
            <section className="container mx-auto">
                <p className="text-gray-600 mb-4">Group not found or invalid type.</p>
            </section>
        );
    }


    let customMessage = null;
    //easter eggs for specific users
    switch (meData?.me?.username) {
        case "bayf":
            customMessage = <div>Salve, Florian!</div>;
            break;
        case "baum":
            customMessage = <div>Servas Martin!</div>;
            break;
        case "troe":
            customMessage = <div className="text-md">Здравия желаю ваше благородие! </div>;
            break;
        default:
            break;
    }

    return (
        <section className="container mx-auto border-t border-neutral-200">
            <div className="my-12">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-semibold ">
                        {(customMessage ==null ? (
                            <>Dashboard - {group.name}</>
                        ) : (
                            <>
                                {customMessage}
                            </>
                        ))}
                    </h1>
                    <div className="toggle-wrapper flex flex-col items-end">
                        <Toggle
                            id="editableToggle"
                            labelText="Bearbeitung aktivieren"
                            labelA="Aus"
                            labelB="Ein"
                            toggled={group.editable}
                            onToggle={handleToggle}
                        />
                        <div className="text-xs text-neutral-600 mt-1">
                            {group.editable ? `Aktiviert ${formatDateTime(group.enabledAt)}` : 'Bearbeitung deaktiviert'}
                        </div>
                    </div>
                </div>
                <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="wrap group block w-full text-left bg-white rounded-xl border-2 border-neutral-200 transition-all duration-100">
                        <div className="p-4">
                            <h2 className="text-lg font-semibold mb-2 text-neutral-800">Studierende Schüler</h2>
                            {groupStateData?.getGroupState?.studentsStudying && groupStateData.getGroupState.studentsStudying.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {groupStateData.getGroupState.studentsStudying.map(s => (
                                        <a key={s.id} href={buildStudentHref(s.id)} className="inline-flex items-center px-3 py-1 bg-neutral-100 text-sm text-neutral-800 rounded-full border border-neutral-100 hover:bg-neutral-200">
                                            {s.lastname && s.surname ? `${s.lastname.toUpperCase()} ${s.surname}` : s.lastname || s.surname || ""}
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-neutral-600">
                                    Niemand studiert gerade
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="wrap group block w-full text-left bg-white rounded-xl border-2 border-neutral-200 transition-all duration-100">
                        <div className="p-4">
                            <h2 className="text-lg font-semibold mb-2 text-neutral-800">Schüler</h2>
                            {group.students && group.students.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {group.students.map(s => (
                                        <a key={s.id} href={buildStudentHref(s.id)} className="inline-flex items-center px-3 py-1 bg-neutral-100 text-sm text-neutral-800 rounded-full border border-neutral-100 hover:bg-neutral-200">
                                            {s.lastname && s.surname ? `${s.lastname.toUpperCase()} ${s.surname}` : s.lastname || s.surname || ""}
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-neutral-600">Keine Schüler</div>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </section>
    );
}